import React, { memo } from 'react'

type TraceMarkerIconProps = {
  fillColor?: string
  ellipseColor?: string
  pathColor?: string
  content: number | string
  className?: string
  baseOpacity?: number // default opacity of the marker until it is hovered
}

/**
 * Lightweight SVG marker icon for map locations
 * Optimized for performance with historical trace replay
 * 
 * @param fillColor Background color of the marker
 * @param ellipseColor Color of the inner ellipse
 * @param pathColor Text color for the content
 * @param content Text/number to display inside the marker
 * @param className Optional CSS class name
 * @param baseOpacity Default opacity until hovered
 */
export const TraceMarkerIcon: React.FC<TraceMarkerIconProps> = memo(({
  fillColor = '#000',
  ellipseColor = '#FFF',
  pathColor = '#000',
  content,
  className = '',
  baseOpacity = 1,
  ...props
}) => {
  // Create a unique ID for the filter to prevent conflicts when multiple icons are rendered
  const filterId = `dropshadow-${Math.random().toString(36).substring(2, 9)}`
  
  return (
    <svg 
      width="24"
      height="32"
      viewBox="0 0 34 50" 
      fill="none" 
      xmlns="http://www.w3.org/2000/svg" 
      style={{ opacity: baseOpacity }}
      className={className}
      {...props}
    >
      <defs>
        <filter id={filterId} height="130%">
          <feGaussianBlur in="SourceAlpha" stdDeviation="1.5" /> {/* Reduced blur for better performance */}
          <feOffset dx="1.5" dy="1.5" result="offsetblur" /> {/* Smaller offset */}
          <feComponentTransfer>
            <feFuncA type="linear" slope="0.4" /> {/* Lower opacity shadow */}
          </feComponentTransfer>
          <feMerge>
            <feMergeNode />
            <feMergeNode in="SourceGraphic" />
          </feMerge>
        </filter>
      </defs>
      
      <g filter={`url(#${filterId})`}>
        <path 
          d="M30 15C30 21.8281 20.8594 33.9844 16.8516 39C15.8906 40.1953 14.1094 40.1953 13.1484 39C9.14063 33.9844 0 21.8281 0 15C0 6.71875 6.71875 0 15 0C23.2812 0 30 6.71875 30 15Z" 
          fill={fillColor} 
        />
        <ellipse 
          cx="15" 
          cy="15" 
          rx="13.125" 
          ry="13.0861" 
          fill={ellipseColor} 
        />
      </g>
      
      <text 
        x="15" 
        y="19" 
        textAnchor="middle" 
        dominantBaseline="middle"
        fontSize={typeof content === 'string' && content.length > 1 ? '12' : '16'}
        fill={pathColor} 
        fontWeight="bold"
      >
        {content}
      </text>
    </svg>
  )
})
